import type { ReactNode } from 'react'
import { useEffect, useRef, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

import { ArrowLeftIcon } from '@gto/icons-react/outline/arrow-left'
import { ArrowLeftStartOnRectangleIcon } from '@gto/icons-react/outline/arrow-left-start-on-rectangle'
import { ArrowRightIcon } from '@gto/icons-react/outline/arrow-right'
import { CheckCircleIcon } from '@gto/icons-react/outline/check-circle'
import { IconGtoWizardLogoSigned } from '@gto/ui/assets'
import { Badge } from '@gto/ui/badge'
import { Button } from '@gto/ui/button'
import { cn } from '@gto/ui/cn'
import { Divider } from '@gto/ui/divider'
import { Paper } from '@gto/ui/paper'
import { Typography } from '@gto/ui/typography'

import { useAuth } from '@/hooks/use-auth'

import { AgreementStep } from './agreement-step'
import { HighlightsStep } from './highlights-step'
import { WelcomeStep } from './welcome-step'

const STEP_NAME_IDS = [
  'onboarding.welcome.stepName',
  'onboarding.highlights.stepName',
  'onboarding.agreement.stepName',
] as const

const STEP_COUNT = STEP_NAME_IDS.length
const AGREEMENT_STEP = STEP_COUNT - 1

export type OnboardingWizardProps = {
  /** Whether the accept mutation is in flight (disables the final CTA). */
  isAccepting: boolean
  /** Fired when the user accepts on the final step (consent already required). */
  onAccept: () => void
  /** Inline error surface rendered above the footer (mutation failure). */
  errorSlot?: ReactNode
}

export const OnboardingWizard = ({ isAccepting, onAccept, errorSlot }: OnboardingWizardProps) => {
  const { formatMessage } = useIntl()
  const { onLogout } = useAuth()
  const [step, setStep] = useState(0)
  const [agreed, setAgreed] = useState(false)
  const stepRegionRef = useRef<HTMLDivElement>(null)

  const isFirst = step === 0
  const isAgreement = step === AGREEMENT_STEP
  const canAdvance = !isAgreement || agreed

  const goBack = () => setStep((s) => Math.max(0, s - 1))
  const goNext = () => setStep((s) => Math.min(AGREEMENT_STEP, s + 1))

  const handlePrimary = () => {
    if (isAgreement) {
      onAccept()

      return
    }
    goNext()
  }

  // Move focus to the step region on change so keyboard + screen-reader users
  // land on the new content.
  useEffect(
    function focusStepOnChange() {
      stepRegionRef.current?.focus()
    },
    [step],
  )

  // Esc steps back (a convenience shortcut; the visible Back button is the
  // primary control). Bound on the document so it works wherever focus sits
  // within the wizard, without a key handler on a non-interactive node.
  useEffect(function bindEscapeToGoBack() {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setStep((s) => Math.max(0, s - 1))
      }
    }
    document.addEventListener('keydown', onKeyDown)

    return () => document.removeEventListener('keydown', onKeyDown)
  }, [])

  const backIcon = (
    <ArrowLeftIcon
      height={18}
      width={18}
    />
  )

  const logoutIcon = (
    <ArrowLeftStartOnRectangleIcon
      height={16}
      width={16}
    />
  )

  const primaryEndIcon = isAgreement ? (
    <CheckCircleIcon
      height={18}
      width={18}
    />
  ) : (
    <ArrowRightIcon
      height={18}
      width={18}
    />
  )

  return (
    <div className="flex h-[100dvh] items-center justify-center overflow-hidden bg-gradient-primary-vertical p-6">
      <Paper
        className="flex max-h-[calc(100dvh-48px)] w-full max-w-[640px] flex-col gap-6 overflow-hidden rounded-lg p-8 sm:p-6"
        variant="outlined"
      >
        <div className="flex items-center justify-between gap-4">
          <IconGtoWizardLogoSigned style={{ height: 20, width: 'auto' }} />
          <div className="flex items-center gap-3">
            <Badge color="primary">
              <FormattedMessage id="onboarding.badge" />
            </Badge>
            <Button
              color="secondary"
              data-tst="onboarding-logout"
              size="small"
              startIcon={logoutIcon}
              variant="text"
              onClick={() => onLogout()}
            >
              <FormattedMessage id="nav.logout" />
            </Button>
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <div
            aria-label={formatMessage({ id: 'onboarding.progressLabel' })}
            aria-valuemax={STEP_COUNT}
            aria-valuemin={1}
            aria-valuenow={step + 1}
            className="flex gap-2"
            role="progressbar"
          >
            {STEP_NAME_IDS.map((id, index) => (
              <div
                key={id}
                className={cn(
                  'h-[3px] flex-1 rounded-full transition-colors duration-300',
                  index <= step ? 'bg-primary-main' : 'bg-paper-elevation5',
                )}
              />
            ))}
          </div>
          <Typography
            aria-live="polite"
            color="text.secondary"
            variant="caption"
          >
            <FormattedMessage
              id="onboarding.stepLabel"
              values={{ current: step + 1, total: STEP_COUNT }}
            />
            {' · '}
            <FormattedMessage id={STEP_NAME_IDS[step]} />
          </Typography>
        </div>

        <Divider />

        {/* `key` remounts on step change so the enter animation replays;
            motion-safe gates it behind the OS reduced-motion preference. */}
        <div
          key={step}
          ref={stepRegionRef}
          className="min-h-0 flex-1 overflow-y-auto rounded-md outline-none motion-safe:animate-fade-in-0"
          tabIndex={-1}
        >
          {step === 0 && <WelcomeStep />}
          {step === 1 && <HighlightsStep />}
          {isAgreement && (
            <AgreementStep
              agreed={agreed}
              onAgreedChange={setAgreed}
            />
          )}
        </div>

        {errorSlot}

        <div className="flex items-center justify-between gap-4">
          <Button
            color="secondary"
            data-tst="onboarding-back"
            disabled={isFirst}
            startIcon={backIcon}
            variant="outlined"
            onClick={goBack}
          >
            <FormattedMessage id="onboarding.back" />
          </Button>

          <Button
            color="primary"
            data-tst="onboarding-next"
            disabled={!canAdvance || isAccepting}
            endIcon={primaryEndIcon}
            variant="contained"
            onClick={handlePrimary}
          >
            {isAgreement ? (
              <FormattedMessage id="terms.acceptCta" />
            ) : (
              <FormattedMessage id="onboarding.next" />
            )}
          </Button>
        </div>
      </Paper>
    </div>
  )
}
